import { Check, ChevronDown, Search, X } from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN = 8;
const MENU_OFFSET = 6;

// Minimal, Linear-style custom dropdown. Single-select by default with optional
// multi-select, keyboard-dismissable, closes on outside click. The popover is
// rendered in a portal with fixed positioning so it floats above everything and
// is never clipped by an ancestor's overflow or stacking context.
export const Select = ({
	value,
	onChange,
	options,
	placeholder = "All",
	icon: Icon,
	ariaLabel,
	align = "left",
	className = "",
	style,
	multiple = false,
	multipleLabel,
}) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	// `mounted` keeps the popover in the DOM long enough to play its exit
	// animation; `closing` swaps the enter animation for the exit one.
	const [mounted, setMounted] = useState(false);
	const [closing, setClosing] = useState(false);
	const [position, setPosition] = useState({ top: 0, left: 0, minWidth: 0 });
	const ref = useRef(null);
	const menuRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		if (open) {
			setMounted(true);
			setClosing(false);
			return undefined;
		}
		if (!mounted) return undefined;

		setClosing(true);
		const timer = setTimeout(() => {
			setMounted(false);
			setClosing(false);
			setQuery("");
		}, 150);
		return () => clearTimeout(timer);
	}, [open, mounted]);

	const updatePosition = useCallback(() => {
		if (!ref.current) return;
		const rect = ref.current.getBoundingClientRect();
		const menuWidth = menuRef.current?.offsetWidth || rect.width;
		const top = rect.bottom + MENU_OFFSET;
		const rawLeft = align === "left" ? rect.left : rect.right - menuWidth;
		const left = Math.min(
			Math.max(rawLeft, VIEWPORT_MARGIN),
			window.innerWidth - VIEWPORT_MARGIN - menuWidth,
		);
		setPosition({ top, left, minWidth: rect.width });
	}, [align]);

	useLayoutEffect(() => {
		if (!open) return;
		updatePosition();
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;

		const frameId = window.requestAnimationFrame(() => {
			inputRef.current?.focus();
		});
		return () => window.cancelAnimationFrame(frameId);
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		const handlePointerDown = (event) => {
			if (ref.current?.contains(event.target)) return;
			if (menuRef.current?.contains(event.target)) return;
			setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};
		const handleReposition = () => updatePosition();

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		window.addEventListener("resize", handleReposition);
		document.addEventListener("scroll", handleReposition, true);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("resize", handleReposition);
			document.removeEventListener("scroll", handleReposition, true);
		};
	}, [open, updatePosition]);

	const selectedValues = multiple
		? Array.isArray(value)
			? value
			: value
				? [value]
				: []
		: [];
	const selectedOptions = multiple
		? selectedValues
				.map((selectedValue) =>
					options.find((option) => option.value === selectedValue),
				)
				.filter(Boolean)
		: [];
	const selected = multiple
		? null
		: options.find((option) => option.value === value);
	const label = multiple
		? multipleLabel?.(selectedOptions, selectedValues) ||
			(selectedOptions.length === 1
				? selectedOptions[0].label
				: selectedOptions.length > 1
					? `${selectedOptions.length} selected`
					: placeholder)
		: selected
			? selected.label
			: placeholder;
	const hasValue = multiple ? selectedValues.length > 0 : Boolean(value);
	const canClear = hasValue && options.some((option) => option.value === "");
	const normalizedQuery = query.trim().toLowerCase();
	const filteredOptions = normalizedQuery
		? options.filter((option) =>
				`${option.label ?? ""} ${option.value ?? ""}`
					.toLowerCase()
					.includes(normalizedQuery),
			)
		: options;
	const clearLabel = ariaLabel
		? `Clear ${ariaLabel.charAt(0).toLowerCase()}${ariaLabel.slice(1)}`
		: "Clear selection";
	const toggleOpen = () => setOpen((prev) => !prev);
	const clearSelection = (event) => {
		event.stopPropagation();
		onChange(multiple ? [] : "");
		setQuery("");
		setOpen(false);
	};
	const isSelected = (option) =>
		multiple
			? option.value === ""
				? selectedValues.length === 0
				: selectedValues.includes(option.value)
			: option.value === value;
	const selectOption = (option) => {
		if (!multiple) {
			onChange(option.value);
			setQuery("");
			setOpen(false);
			return;
		}

		if (option.value === "") {
			onChange([]);
			setQuery("");
			setOpen(false);
			return;
		}

		if (selectedValues.includes(option.value)) {
			onChange(selectedValues.filter((item) => item !== option.value));
		} else {
			onChange([...selectedValues, option.value]);
		}
	};

	const menu =
		mounted &&
		createPortal(
			<div
				ref={menuRef}
				className={`fixed z-[9999] min-w-[12rem] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-1 shadow-xl duration-150 ease-out ${
					closing
						? "pointer-events-none animate-out fade-out zoom-out-95 slide-out-to-top-1"
						: "animate-in fade-in zoom-in-95 slide-in-from-top-1"
				}`}
				style={{
					top: `${position.top}px`,
					left: `${position.left}px`,
					minWidth: `${position.minWidth}px`,
				}}
			>
				<div className="select-search-shell mb-1 flex h-9 items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2.5">
					<Search className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
					<input
						ref={inputRef}
						type="text"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Search"
						className="select-search-input h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none"
					/>
					{query && (
						<button
							type="button"
							onClick={() => {
								setQuery("");
								inputRef.current?.focus();
							}}
							className="no-hover-lift inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
							aria-label="Clear dropdown search"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
				<div className="max-h-64 overflow-auto">
					{filteredOptions.length > 0 ? (
						filteredOptions.map((option) => {
							const active = isSelected(option);
							return (
								<button
									key={option.value || "__all"}
									type="button"
									onClick={() => selectOption(option)}
									className={`no-hover-lift flex h-8 w-full items-center justify-between gap-2 rounded-md px-2.5 text-left text-xs transition-colors ${
										active
											? "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
											: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
									}`}
								>
									<span className="truncate">{option.label}</span>
									{active && (
										<Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-accent)]" />
									)}
								</button>
							);
						})
					) : (
						<div className="px-2.5 py-3 text-xs text-[var(--color-text-secondary)]">
							No options
						</div>
					)}
				</div>
			</div>,
			document.body,
		);

	return (
		<div
			ref={ref}
			style={style}
			className={`relative inline-flex min-w-0 ${className || "w-[9.75rem] max-w-full"}`}
		>
			<div
				className={`inline-flex h-8 w-full min-w-0 items-center overflow-hidden rounded-lg border text-xs font-medium transition-colors focus-within:border-[var(--color-border-hover)] ${
					hasValue
						? "border-[var(--color-border-hover)] bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
						: "border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-primary)]"
				}`}
			>
				<button
					type="button"
					aria-expanded={open}
					aria-label={ariaLabel}
					onClick={toggleOpen}
					className="no-hover-lift inline-flex h-full min-w-0 flex-1 items-center gap-1.5 px-2.5 text-left transition-colors focus:outline-none"
				>
					{Icon && <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />}
					<span className="min-w-0 flex-1 truncate">{label}</span>
					<ChevronDown
						className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200 ${
							open ? "rotate-180" : ""
						}`}
					/>
				</button>
				{canClear && (
					<button
						type="button"
						onClick={clearSelection}
						className="no-hover-lift inline-flex h-full w-7 shrink-0 items-center justify-center border-l border-[var(--color-border)] text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none"
						aria-label={clearLabel}
						title={clearLabel}
					>
						<X className="h-3.5 w-3.5" />
					</button>
				)}
			</div>
			{menu}
		</div>
	);
};

export default Select;
